import { Api } from '../../api/models/apis';
import { Asset } from '../../api/models/assets';
import { FORM_DATATYPES, TpAutoFormDef, promptAutoForm } from '../../components/auto-form-modal/AutoForm';
import { TpConfirmData, promptConfirmationBox } from '../../components/message-boxes/ConfirmBox';
import { getAssetRelations } from '../../data/assets/helpers';
import { TpAssetInfo, TpAssetRelationInfo, TpCollapsedRelationSet } from '../../data/assets/interfaces';
import { TpQueriedAssetsData } from '../../data/assets/queriedAssetsSlice';

export function promptCreateAsset(assetOntology: any, api: Api): Promise<TpAssetInfo> {
  const form: TpAutoFormDef = {
    title: `Create new ${assetOntology.displayName}`,
    fields: [
      {
        id: 'displayName',
        name: 'Display Name',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        required: true,
      },
      {
        id: 'description',
        name: 'Description',
        dataType: FORM_DATATYPES.STRING,
        startContent: '',
        lineCount: 4,
      },
      {
        id: 'tags',
        name: 'Tags',
        dataType: FORM_DATATYPES.TAGS,
        startContent: [],
      },

      {
        id: 'properties',
        name: 'Custom Properties',
        dataType: FORM_DATATYPES.YAML,
        startContent: '',
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    const name = dataForm.displayName.replace(/\s+/g, '_').toLowerCase();
    const newAsset: Asset = {
      type: assetOntology.identifier,
      name: name,
      displayName: dataForm.displayName,
      description: dataForm.description,
      user_tags: dataForm.tags,
      properties: dataForm.properties ? dataForm.properties : {},
    };
    return api.asset.addAsset(newAsset).then((resp) => {
      return resp;
    });
  });
}

export function promptDeleteAsset(asset: TpAssetInfo, api: Api, queriedAssetsData: TpQueriedAssetsData): Promise<void> {
  const confirmData: TpConfirmData = {
    title: `Delete ${asset.displayName}`,
    description: `Do you really want to delete  ${asset.displayName}? \n\n`,
  };
  const relations = getAssetRelations(asset.id, queriedAssetsData);
  if (relations.length > 0) {
    confirmData.description += 'This will also delete the following relation(s): \n';
    relations.forEach((relation) => {
      confirmData.description += `- Relation to ${relation.toAsset.displayName} \n`;
    });
  }

  return promptConfirmationBox(confirmData).then((confirmed) => {
    if (confirmed) {
      return api.asset.deleteAsset(asset.id);
    }
  });
}

export function promptUpdateAsset(
  asset: TpAssetInfo,
  api: Api,
  displayName: string,
  currentValue: string,
  key: string,
): Promise<TpAssetInfo> {
  const form: TpAutoFormDef = {
    title: `Update ${asset.displayName}`,
    fields: [
      {
        id: key,
        name: displayName,
        dataType: FORM_DATATYPES.STRING,
        startContent: currentValue || '',
        required: true,
        lineCount: key === 'description' ? 4 : 1,
      },
    ],
  };

  return promptAutoForm(form).then((dataForm: any) => {
    if (key.startsWith('properties')) {
      const keyArray = key.split('.');
      const propertyInfo = { key: [keyArray[keyArray.length - 1]], value: dataForm[key] };
      return api.asset.setAssetProperty(asset.id, propertyInfo).then((resp) => {
        return resp;
      });
    } else {
      const updates = { [key]: dataForm[key] };
      return api.asset.modifyAsset(asset.id, updates).then((resp) => {
        return resp;
      });
    }
  });
}

export function promptAddRelationWizard(
  fromAsset: TpAssetInfo,
  existingRelations: TpAssetRelationInfo[],
  ontology: any,
  api: Api,
  isReverse: boolean,
  queriedAssetsData: TpQueriedAssetsData,
): Promise<any> {
  const form: TpAutoFormDef = {
    title: `Add relation for ${fromAsset.displayName}`,
    fields: [],
    actionButtons: [{ id: 'next', content: 'Next', isWizard: true }],
  };

  const ontologyRelations: { [key: string]: any } = {};
  Object.values(ontology.assetTypes).forEach((assetType: any) => {
    if (assetType.relations) {
      assetType.relations.forEach((relation: any) => {
        if (relation.userCanCreate) {
          ontologyRelations[relation.identifier] = {
            fromAsset: assetType.identifier,
            fromAssetDisplayName: assetType.displayName,
            toAssetDisplayName: ontology.assetTypes[relation.toAsset].displayName,
            toAsset: relation.toAsset,
            cardinality: relation.cardinality,
          };
        }
      });
    }
  });
  if (isReverse) {
    form.fields.push({
      id: 'relationType',
      name: 'Relation to',
      dataType: FORM_DATATYPES.MULTIPLECHOICE,
      startContent: '',
      choices: Object.entries(ontologyRelations)
        .filter(([relation_id, relation]) => relation.fromAsset === fromAsset.type)
        .map(([relation_id, relation]) => {
          return {
            id: relation_id,
            content: relation.toAssetDisplayName,
          };
        }),
    });
  } else {
    form.fields.push({
      id: 'relationType',
      name: 'Relation to',
      dataType: FORM_DATATYPES.MULTIPLECHOICE,
      startContent: '',
      choices: Object.entries(ontologyRelations)
        .filter(([relation_id, relation]) => relation.toAsset === fromAsset.type)
        .map(([relation_id, relation]) => {
          return {
            id: relation_id,
            content: relation.fromAssetDisplayName,
          };
        }),
    });
  }

  return promptAutoForm(form)
    .then((dataForm: any) => {
      return {
        fromAsset: fromAsset,
        existingRelations: existingRelations,
        relationType: dataForm.relationType,
        relation: ontologyRelations[dataForm.relationType],
        api: api,
        queriedAssetsData: queriedAssetsData,
        isReverse: isReverse,
      };
    })
    .then(
      (data: {
        fromAsset: TpAssetInfo;
        existingRelations: TpAssetRelationInfo[];
        relation: any;
        relationType: string;
        api: Api;
        queriedAssetsData: TpQueriedAssetsData;
        isReverse: boolean;
      }) => {
        return promptAddRelationWizardStep2(data);
      },
    );
}

export function promptAddRelationWizardStep2(data: {
  fromAsset: TpAssetInfo;
  existingRelations: TpAssetRelationInfo[];
  relation: any;
  relationType: string;
  api: Api;
  queriedAssetsData: TpQueriedAssetsData;
  isReverse: boolean;
}): Promise<TpAssetRelationInfo | unknown> {
  const { fromAsset, existingRelations, relation, relationType, api, queriedAssetsData, isReverse } = data;
  const toAssetType = relation.fromAsset === fromAsset.type ? relation.toAsset : relation.fromAsset;
  const relationDisplayName = isReverse ? relation.toAssetDisplayName : relation.fromAssetDisplayName;
  const existingRelationsByType = existingRelations
    .filter((rel) => rel.type === relationType)
    .map((rel) => rel.toAsset.id);
  const limit = queriedAssetsData.queryStats[toAssetType];
  let form: TpAutoFormDef = {
    title: `No Potential relation found for ${fromAsset.displayName} and ${relationDisplayName}`,
    fields: [],
    actionButtons: [{ id: 'cancel', content: 'Cancel' }],
  };

  if (!limit || limit === 0) {
    return promptAutoForm(form);
  }

  return api.asset
    .listAssets({ limit: limit, offset: 0, typeFilter: toAssetType })
    .then((response) => {
      const assets = response.assets;
      const potentialRelatedAssets = assets.filter((item) => !existingRelationsByType.includes(item.id));
      if (potentialRelatedAssets.length > 0) {
        form = {
          title: `Select related Asset for ${fromAsset.displayName}`,
          fields: [
            {
              id: 'toAsset',
              name: 'Related asset',
              dataType: FORM_DATATYPES.SEARCH,
              startContent: '',
              choices: potentialRelatedAssets.map((asset: TpAssetInfo) => {
                return {
                  id: asset.id,
                  content: asset.displayName,
                };
              }),
            },
          ],
        };
      }
      return promptAutoForm(form);
    })
    .then((dataForm: any) => {
      return api.relation.addRelation(fromAsset.id, dataForm.toAsset, relationType, isReverse);
    });
}

export function promptDeleteRelation(relation: TpCollapsedRelationSet, api: Api): Promise<string> {
  let form: TpAutoFormDef = {
    title: `Are You Sure You Want to Delete the Relation to ${relation.relations[0].toAsset.displayName}?`,
    fields: [],
    actionButtons: [
      { id: 'cancel', content: 'Cancel' },
      { id: 'delete', content: 'Delete' },
    ],
  };
  if (relation.relations.length > 1) {
    form = {
      title: `Choose Which Relation to Delete:`,
      fields: [
        {
          id: 'relationId',
          name: 'Relation to',
          dataType: FORM_DATATYPES.MULTIPLECHOICE,
          startContent: '',
          choices: relation.relations.map((relation: any) => {
            return {
              id: relation.id,
              content: relation.toAssetName,
            };
          }),
        },
      ],
      actionButtons: [
        { id: 'cancel', content: 'Cancel' },
        { id: 'delete', content: 'Delete' },
      ],
    };
  }
  return promptAutoForm(form).then((dataForm: any) => {
    if (relation.relations.length > 1) {
      const relationToDelete = relation.relations.find((rel) => rel.id === dataForm.relationId);
      return api.relation.deleteRelation(relationToDelete!.id).then(() => {
        return relationToDelete!.id;
      });
    }
    return api.relation.deleteRelation(relation.relations[0].id).then(() => {
      return relation.relations[0].id;
    });
  });
}
